import { QueryClient, useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import CompanyHotelService from '@app/model/company_hotel/service/CompanyHotelService';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import { CompanyHotelQueryKeys } from '@app/model/company_hotel/service/CompanyHotelQueryKeys';
import { CompanyHotelCompetitorsOrderInput } from '@app/model/company_hotel/input/CompanyHotelCompetitorsOrderInput';
import CompanyHotelParsingSettingInput from '@app/model/company_hotel/input/CompanyHotelParsingSettingInput';
import DataProvider from '@app/model/_common/DataProvider';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import CompanyHotelInput from '@app/model/company_hotel/input/CompanyHotelInput';
import Return from '@app/model/_common/Return';
import CompanyHotelMetaradarSettingsInput from '@app/model/company_hotel/input/CompanyHotelMetaradarSettingsInput';
import SaveOrCreateCompetitorsGroupInput from '@app/model/company_hotel/input/SaveOrCreateCompetitorsGroupInput';
import CompanyHotelLite from '@app/model/company_hotel/CompanyHotelLite';
import CompanyHotelMutationResult from '@app/model/company_hotel/CompanyHotelMutationResult';
import ProcessRequestExecutionState from '@app/model/process-request/ProcessRequestExecutionState';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';

/**
 * A React Query hook for fetching a single company hotel by its ID.
 * @param companyHotelId The ID of the company hotel to fetch.
 * @param options Optional react-query options.
 */
export function useCompanyHotelQuery(companyHotelId: number, options?: Omit<UseQueryOptions<CompanyHotel, unknown, CompanyHotel | undefined>, 'queryKey' | 'queryFn'>) {
	return useQuery({
		queryKey: CompanyHotelQueryKeys.detail(companyHotelId),
		queryFn: () => CompanyHotelService.findCompanyHotelWithCompetitors(companyHotelId),
		networkMode: 'offlineFirst',
		refetchOnWindowFocus: false,
		...options,
		// The select option ensures the data is always a class instance, even after cache rehydration.
		select: (data: CompanyHotel | Record<string, any>) => {
			if (!data) {
				return undefined;
			}
			return data instanceof CompanyHotel ? data : new CompanyHotel(data as CompanyHotel);
		},
	});
}

/**
 * A React Query hook for fetching all company hotels.
 * @param options Optional react-query options.
 */
export function useCompanyHotelsQuery(options?: Omit<UseQueryOptions<CompanyHotel[], unknown, CompanyHotel[]>, 'queryKey' | 'queryFn'>) {
	return useQuery({
		queryKey: CompanyHotelQueryKeys.all,
		queryFn: () => CompanyHotelService.findCompanyHotels(),
		...options,
		select: (data: (CompanyHotel | Record<string, any>)[]) => {
			if (!data) {
				return [];
			}
			return data.map(item => item instanceof CompanyHotel ? item : new CompanyHotel(item as CompanyHotel));
		},
	});
}

export function useCompanyHotelsLiteQuery(options?: Omit<UseQueryOptions<CompanyHotelLite[], unknown, CompanyHotelLite[]>, 'queryKey' | 'queryFn'>) {
	return useQuery({
		queryKey: CompanyHotelQueryKeys.list(),
		queryFn: () => CompanyHotelService.findCompanyHotelsLite(),
		...options,
	});
}

// Mutations

export function useCreateOrSaveCompanyHotelMutation(options?: {
	isNew?: boolean,
	onMutate?: (variables: CompanyHotelInput) => any,
	onSuccess?: (data: CompanyHotelMutationResult) => any,
	onError?: (error: any) => any
}) {
	const { isNew, onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: isNew ? CompanyHotelService.createCompanyHotel : CompanyHotelService.saveCompanyHotel,
		onMutate: (variables) => {
			onMutate?.(variables);
		},
		onSuccess: (data) => {
			replaceCompanyHotelInCache(queryClient, data.companyHotel);
			void invalidateCompetitorImportances(queryClient, data.companyHotel.id);
			onSuccess?.(data);
		},
		onError: (error) => {
			onError?.(error);
		},
	});
}

export function useCreateCompanyHotelMutation(options?: {
	onMutate?: (variables: CompanyHotelInput) => any,
	onSuccess?: (data: CompanyHotelMutationResult) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelService.createCompanyHotel,
		onMutate: (variables) => {
			onMutate?.(variables);
		},
		onSuccess: (data) => {
			replaceCompanyHotelInCache(queryClient, data.companyHotel);
			void invalidateCompetitorImportances(queryClient, data.companyHotel.id);
			onSuccess?.(data);
		},
		onError: (error) => {
			onError?.(error);
		},
	});
}

export function useSaveCompanyHotelMutation(options?: {
	onMutate?: (variables: CompanyHotelInput) => any,
	onSuccess?: (data: CompanyHotelMutationResult) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelService.saveCompanyHotel,
		onMutate: (variables) => {
			onMutate?.(variables);
		},
		onSuccess: (data) => {
			replaceCompanyHotelInCache(queryClient, data.companyHotel);
			void invalidateCompetitorImportances(queryClient, data.companyHotel.id);
			onSuccess?.(data);
		},
		onError: (error) => {
			onError?.(error);
		},
	});
}

export function useRemoveCompanyHotelCompetitorsGroupMutation(options?: {
	onMutate?: (variables: { companyHotelId: number, competitorsGroupId: number }) => any,
	onSuccess?: (data: CompanyHotel) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelService.removeCompanyHotelCompetitorsGroup,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data) => {
			replaceCompanyHotelInCache(queryClient, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useSaveOrCreateCompanyHotelCompetitorsGroupMutation(options?: {
	onMutate?: (variables: { input: SaveOrCreateCompetitorsGroupInput }) => any,
	onSuccess?: (data: CompanyHotel) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelService.saveOrCreateCompanyHotelCompetitorsGroup,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data) => {
			replaceCompanyHotelInCache(queryClient, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useDisableCompanyHotelMutation(options?: {
	onMutate?: (variables: number) => any,
	onSuccess?: (data: any) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelService.disableCompanyHotel,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data) => {
			queryClient.setQueryData<CompanyHotel[]>(CompanyHotelQueryKeys.all, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useUpdateCompanyHotelSubscriptionLastLoginDateMutation(options?: {
	onMutate?: (variables: { companyHotelId: number }) => any,
	onSuccess?: (data: Return) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	return useMutation({
		mutationFn: CompanyHotelService.updateCompanyHotelSubscriptionLastLoginDate,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data) => {
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useSaveCompanyMetaradarHotelSettings(options?: {
	onMutate?: (variables: { companyHotelId: number, input: CompanyHotelMetaradarSettingsInput }) => any,
	onSuccess?: (data: CompanyHotel) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelService.saveCompanyMetaradarHotelSettings,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data) => {
			replaceCompanyHotelInCache(queryClient, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useSaveCompanyHotelParsingSettingMutation(options?: {
	onMutate?: (variables: { companyHotelId: number, input: CompanyHotelParsingSettingInput }) => any,
	onSuccess?: (data: CompanyHotel) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelService.saveCompanyHotelParsingSetting,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data) => {
			replaceCompanyHotelInCache(queryClient, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useUpdateCompanyHotelRoomMappingTypeMutation(options?: {
	onMutate?: (variables: {
		companyHotelId: number,
		hotelId: number,
		roomId: number,
		type: HotelRoomMappingType
	}) => any,
	onSuccess?: (data: CompanyHotel) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelService.updateCompanyHotelRoomMappingType,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data) => {
			replaceCompanyHotelInCache(queryClient, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useAutoUpdateCompanyHotelRoomMappingTypesMutation(options?: {
	onMutate?: (variables: { companyHotelId: number, hotelId: number }) => any,
	onSuccess?: (data: CompanyHotel) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelService.autoUpdateCompanyHotelRoomMappingTypes,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data) => {
			replaceCompanyHotelInCache(queryClient, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useEnrichHotelRoomsMutation(options?: {
	onSuccess?: (data: ProcessRequestExecutionState[], variables: { companyHotelId: number, hotelId: number }) => any,
	onError?: (error: any) => any
}) {
	const { onSuccess, onError } = options || {};
	return useMutation({
		mutationFn: CompanyHotelService.enrichHotelRooms,
		onSuccess: (data, variables) => onSuccess?.(data, variables),
		onError: (error) => onError?.(error),
	});
}

export function useEnableCompanyHotelDataProviderMutation(options?: {
	onMutate?: (variables: { companyHotelId: number, provider: DataProvider }) => any,
	onSuccess?: (data: CompanyHotel) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelService.enableCompanyHotelDataProvider,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data) => {
			replaceCompanyHotelInCache(queryClient, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useReorderCompanyHotelCompetitorsMutation(options?: {
	onMutate?: (variables: CompanyHotelCompetitorsOrderInput) => any,
	onSuccess?: (data: Return) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelService.reorderCompanyHotelCompetitors,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: CompanyHotelQueryKeys.all }).then(() => {
				if (!!onSuccess) onSuccess(data);
			});
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useSaveCompanyHotelRoomMappingTypeConfigMutation(options?: {
	onMutate?: (variables: {
		companyHotelId: number,
		type: HotelRoomMappingType,
		name: string
	}) => any,
	onSuccess?: (data: CompanyHotel) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelService.saveCompanyHotelRoomMappingTypeConfig,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data) => {
			replaceCompanyHotelInCache(queryClient, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useDisableCompanyHotelRoomMappingTypeConfigMutation(options?: {
	onMutate?: (variables: {
		companyHotelId: number,
		type: HotelRoomMappingType
	}) => any,
	onSuccess?: (data: CompanyHotel) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelService.disableCompanyHotelRoomMappingTypeConfig,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data) => {
			replaceCompanyHotelInCache(queryClient, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useCreateCompanyHotelCompetitorTagMutation(options?: {
	onMutate?: (variables: {
		companyHotelId: number,
		name: string,
		color: string
	}) => any,
	onSuccess?: (data: CompanyHotel) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelService.createCompanyHotelCompetitorTag,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data) => {
			replaceCompanyHotelInCache(queryClient, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useEditCompanyHotelCompetitorTagMutation(options?: {
	onMutate?: (variables: {
		companyHotelId: number,
		competitorTagId: number,
		name: string,
		color: string
	}) => any,
	onSuccess?: (data: CompanyHotel) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelService.editCompanyHotelCompetitorTag,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data) => {
			replaceCompanyHotelInCache(queryClient, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useDeleteCompanyHotelCompetitorTagMutation(options?: {
	onMutate?: (variables: {
		companyHotelId: number,
		competitorTagId: number
	}) => any,
	onSuccess?: (data: CompanyHotel) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelService.deleteCompanyHotelCompetitorTag,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data) => {
			replaceCompanyHotelInCache(queryClient, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useAddCompanyHotelCompetitorTagToCompetitorMutation(options?: {
	onMutate?: (variables: {
		companyHotelId: number,
		competitorId: number,
		competitorTagId: number
	}) => any,
	onSuccess?: (data: CompanyHotel) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelService.addCompanyHotelCompetitorTagToCompetitor,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data) => {
			replaceCompanyHotelInCache(queryClient, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useRemoveCompanyHotelCompetitorTagFromCompetitorMutation(options?: {
	onMutate?: (variables: {
		companyHotelId: number,
		competitorId: number,
		competitorTagId: number
	}) => any,
	onSuccess?: (data: CompanyHotel) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelService.removeCompanyHotelCompetitorTagFromCompetitor,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data) => {
			replaceCompanyHotelInCache(queryClient, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function useSanitizeRoomMappingsMutation(options?: {
	onMutate?: (variables: {
		companyHotelId: number,
		hotelId: number,
	}) => any,
	onSuccess?: (data: CompanyHotel) => any,
	onError?: (error: any) => any
}) {
	const { onMutate, onSuccess, onError } = options || {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: CompanyHotelService.sanitizeRoomMappings,
		onMutate: (variables) => {
			if (!!onMutate) onMutate(variables);
		},
		onSuccess: (data) => {
			replaceCompanyHotelInCache(queryClient, data);
			if (!!onSuccess) onSuccess(data);
		},
		onError: (error) => {
			if (!!onError) onError(error);
		},
	});
}

export function replaceCompanyHotelInCache(queryClient: QueryClient, data: CompanyHotel) {
	const newCompanyHotel = new CompanyHotel(data);
	queryClient.setQueryData<CompanyHotel[]>(CompanyHotelQueryKeys.all, (old) => {
		if (!old) return [newCompanyHotel];
		const index = old.findIndex((companyHotel) => companyHotel.id === newCompanyHotel.id);
		if (index === -1) return [...old, newCompanyHotel];
		return [...old.slice(0, index), newCompanyHotel, ...old.slice(index + 1)];
	});
	queryClient.setQueryData(CompanyHotelQueryKeys.detail(newCompanyHotel.id), newCompanyHotel);
}

function invalidateCompetitorImportances(queryClient: QueryClient, companyHotelId: number) {
	return queryClient.invalidateQueries({
		queryKey: PricingAutomationQueryKeys.pricingStrategyCompetitorImportances(companyHotelId),
	});
}
